import { Action, BasicEditingSupport } from 'common/table/editing-support.interface';
import { ApiProductRevision, ApiV1Service, DeclarationStatus } from 'api';
import { firstValueFrom } from 'rxjs';
import { inject } from '@angular/core';
import { AddRevisionComponent } from 'component/details/add/add-revision.component';

export class RevisionsEditingSupport extends BasicEditingSupport<ApiProductRevision> {
  private apiService = inject(ApiV1Service);
  private readonly productId: number;

  constructor(productId: number) {
    super();
    this.productId = productId;
  }

  CREATE_REVISION_ACTION: Action<ApiProductRevision> = {
    id: 'addRevision',
    scopes: ['menu'],
    icon: 'note_add',
    labelMessageKey: 'revisions.add',
    disabled: () => this.productId === undefined,
    on: async () => {
      if (this.productId === undefined) {
        return;
      }

      const dialogRef = this.dialog.open(AddRevisionComponent, {
        data: {
          productId: this.productId,
        },
      });

      await firstValueFrom(dialogRef.afterClosed());
    },
  };

  OPEN_REVISION_ACTION: Action<ApiProductRevision> = {
    id: 'openRevision',
    scopes: ['contextMenu'],
    icon: 'open_in_new',
    labelMessageKey: 'revisions.open',
    disabled: (revision?: ApiProductRevision) => !revision?.id,
    on: async (revision?: ApiProductRevision) => {
      if (!revision?.id) {
        return;
      }

      await this.router.navigate([revision.id], { relativeTo: this.route.parent });
    },
  };

  SHOW_SELF_DECLARATION: Action<ApiProductRevision> = {
    id: 'showSelfDeclaration',
    scopes: ['contextMenu'],
    icon: 'policy',
    labelMessageKey: 'revisions.selfDeclaration',
    disabled: (revision?: ApiProductRevision) => !this.hasValidDeclarationStatus(revision),
    on: async (revision?: ApiProductRevision) => {
      if (!revision?.id) {
        return;
      }

      await this.router.navigate(['/products', this.productId, revision.id, 'declared-compliant']);
    },
  };

  private hasValidDeclarationStatus(revision?: ApiProductRevision): boolean {
    return revision?.declarationStatus === DeclarationStatus.Valid;
  }

  override getActions(): Action<ApiProductRevision>[] {
    return [
      this.CREATE_REVISION_ACTION,
      this.OPEN_REVISION_ACTION,
      this.SHOW_SELF_DECLARATION,
      ...super.getActions(),
    ];
  }

  async deleteByIds(ids: number[]): Promise<void> {
    await firstValueFrom(this.apiService.deleteProductRevisions(ids));
  }

  override getItemDisplayName(item: ApiProductRevision): string {
    return String(item.revision);
  }
}
